import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { Title, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import useAuthorize from '../hooks/useAuthorize';
import { WHATSAPP_MODULE_ROLES } from '../roles/portalRoles';
import { fetchJobs } from './whatsappBroadcastApi';
import { BroadcastJob } from './types';

const statusColor = (status: string) => {
  switch (status) {
    case 'running': return 'info';
    case 'completed': return 'success';
    case 'failed': return 'error';
    case 'cancelled': return 'default';
    default: return 'warning';
  }
};

const WhatsAppBroadcastList = () => {
  useAuthorize(WHATSAPP_MODULE_ROLES);
  const notify = useNotify();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<BroadcastJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const perPage = 25;

  const load = async () => {
    setLoading(true);
    try {
      const result = await fetchJobs(page, perPage);
      setJobs(result.jobs);
      setTotal(result.total);
    } catch (error) {
      console.error(error);
      notify('Failed to load broadcast jobs', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page]);

  const progress = (job: BroadcastJob) => {
    if (!job.total_recipients) return 0;
    return Math.min(100, Math.round((job.sent_count / job.total_recipients) * 100));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title="WhatsApp Broadcasts" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Launch and monitor buyer WhatsApp broadcast jobs via Twilio or Plivo.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/whatsapp-broadcasts/new')}>
            Create new job
          </Button>
        </Box>
      </Box>

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Template</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Delivered</TableCell>
              <TableCell>Read</TableCell>
              <TableCell>Failed</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow
                key={job.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/whatsapp-broadcasts/${job.id}`)}
              >
                <TableCell>{job.template_name}</TableCell>
                <TableCell>{job.provider}</TableCell>
                <TableCell>
                  <Chip size="small" label={job.status} color={statusColor(job.status) as any} />
                </TableCell>
                <TableCell sx={{ minWidth: 180 }}>
                  <Typography variant="body2">{job.sent_count} / {job.total_recipients}</Typography>
                  <LinearProgress variant="determinate" value={progress(job)} sx={{ mt: 0.5 }} />
                </TableCell>
                <TableCell>{job.delivered_count}</TableCell>
                <TableCell>{job.read_count}</TableCell>
                <TableCell>{job.failed_count}</TableCell>
                <TableCell>{new Date(job.dt_created).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {!loading && jobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>No broadcast jobs yet</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
        <Typography variant="body2">Page {page + 1} · {total} jobs</Typography>
        <Button disabled={(page + 1) * perPage >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </Box>
    </Box>
  );
};

export default WhatsAppBroadcastList;
